import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MdDashboard, MdMenuBook, MdPeople, MdFactCheck, MdExpandMore, MdEventBusy, MdLibraryBooks, MdNotificationsActive } from 'react-icons/md';
import AuthService from '../services/authService';

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '64px')};
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;
`;

const BrandBadge = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #10b981; /* emerald-500 */
  color: #fff;
  font-weight: 700;
  font-size: 12px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: ${(p) => (p.$active ? '#ecfdf5' : 'transparent')};
  color: ${(p) => (p.$active ? '#065f46' : '#374151')};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#fff' : '#374151')};
  background: ${(p) => (p.$active ? '#10b981' : 'transparent')};
  text-decoration: none;
  font-size: 14px;
`;

const ChildWrap = styled.div`
  margin-top: 4px;
  padding-left: 24px;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const Footer = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 16px;
  color: #6b7280;
  font-size: 12px;
`;

export default function LecturerSidebar({ isOpen }) {
  const [expanded, setExpanded] = useState([]);
  const user = AuthService.getUser();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  const navigation = useMemo(() => ([
    { name: 'Dashboard', href: '/lecturer/dashboard', icon: <MdDashboard /> },
    { name: 'My Correction', href: '/lecturer/correction', icon: <MdMenuBook /> },
    { name: 'My Feedback', href: '/lecturer/my-feedback', icon: <MdPeople /> },
    { name: 'Attendance', href: '/lecturer/attendance', icon: <MdFactCheck /> },
    { name: 'Support Task', href: '/lecturer/support-task', icon: <MdMenuBook /> },
    { name: 'Notify HOD', href: '/lecturer/notify-hod', icon: <MdNotificationsActive /> },
    { name: 'Shared Courses', href: '/lecturer/shared-courses', icon: <MdLibraryBooks /> },
    { name: 'My Unavailability', href: '/lecturer/unavailability', icon: <MdEventBusy /> },
    { name: 'Profile', href: '/lecturer/profile', icon: <MdPeople /> },
  ]), []);

  useEffect(() => {
    const activeParents = [];
    navigation.forEach((item) => {
      if (item.children && item.children.some((child) => isActive(child.href))) {
        activeParents.push(item.name);
      }
    });
    if (activeParents.length) {
      setExpanded((prev) => Array.from(new Set([...prev, ...activeParents])));
    }
  }, [currentPath, navigation]);

  const isActive = (href) => {
    if (href === '/lecturer/dashboard') return currentPath === href;
    return currentPath.startsWith(href) && href !== '#';
  };

  const toggleExpanded = (name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  return (
    <Aside $open={isOpen}>
      <TopBar>
        {isOpen ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BrandBadge>LC</BrandBadge>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Lecturer</span>
          </div>
        ) : (
          <BrandBadge>LC</BrandBadge>
        )}
      </TopBar>
      <Nav>
        {navigation.map((item) => {
          const hasChildren = !!(item.children && item.children.length);
          const itemActive = isActive(item.href) || (item.children && item.children.some((c) => isActive(c.href)));
          if (hasChildren) {
            const isExpanded = expanded.includes(item.name);
            return (
              <div key={item.name} style={{ marginBottom: 6 }}>
                <NavButton onClick={() => toggleExpanded(item.name)} $active={itemActive}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{item.icon}</span>
                    {isOpen && <span>{item.name}</span>}
                  </span>
                  {isOpen && (
                    <MdExpandMore style={{ fontSize: 18, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  )}
                </NavButton>
                {isOpen && (
                  <ChildWrap $open={isExpanded}>
                    {item.children.map((child) => {
                      const childActive = isActive(child.href);
                      return (
                        <div key={child.name} style={{ marginBottom: 4 }}>
                          <NavLink href={child.href} $active={childActive}>
                            <span>{child.icon}</span>
                            <span>{child.name}</span>
                          </NavLink>
                        </div>
                      );
                    })}
                  </ChildWrap>
                )}
              </div>
            );
          }
          return (
            <div key={item.name} style={{ marginBottom: 6 }}>
              <NavLink href={item.href} $active={itemActive}>
                <span>{item.icon}</span>
                {isOpen && <span>{item.name}</span>}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 600 }}>Lecturer Panel</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
